import { getFormatter, isDefined, isLimitOrderType, isStopActivationOrderType } from '@eih-netbroker/common';
import { memo, useCallback, useMemo } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { SuggestedPrices } from '../../../services/order/useSuggestedPrices';
import { OrderFormData, PriceValidationState } from './OrderForm2';
import { OrderFormNumberInput } from './OrderFormNumberInput';
import classes from './OrderFormPriceInputs.module.scss';
import { PricePresetSelector } from './OrderFormPricePresetSelector';

const unknownValue = '-';
const PRICE_INPUT_MAX_FONT_SIZE = 36;
const PRICE_INPUT_SUFFIX_MAX_FONT_SIZE = 20;
const PRICE_INPUT_DECIMAL_SCALE = 4;

type OrderFormPriceInputsProps = {
  order: OrderFormData;
  onChange: (key: 'price' | 'stopActivationPrice', value: number | undefined) => void;
  isLoading: boolean;
  readOnly: boolean;
  disabled: boolean;
  suggestedPrices: SuggestedPrices;
  priceValidationState?: PriceValidationState;
  stopActivationPriceValidationState?: PriceValidationState;
};

export const OrderFormPriceInputs = memo(
  ({
    order,
    onChange,
    isLoading,
    readOnly,
    disabled,
    suggestedPrices,
    priceValidationState,
    stopActivationPriceValidationState,
  }: OrderFormPriceInputsProps) => {
    const { translation } = useLocale();
    const { formatCurrency, formatPercentage } = getFormatter();

    const lastPrice = suggestedPrices.lastPrice;

    const handleLimitPriceChange = useCallback(
      (value: number | undefined): void => onChange('price', value),
      [onChange],
    );

    const handleStopPriceChange = useCallback(
      (value: number | undefined): void => onChange('stopActivationPrice', value),
      [onChange],
    );

    const limitPriceDistance = useMemo(() => {
      if (!isDefined(order.price) || !isDefined(lastPrice) || lastPrice === 0) return unknownValue;
      const distance = order.price - lastPrice;
      const percent = distance / lastPrice;
      return `${formatCurrency(distance, order.instrument ?? null)} (${formatPercentage(percent)})`;
    }, [order.price, order.instrument, lastPrice, formatCurrency, formatPercentage]);

    const stopPriceDistance = useMemo(() => {
      if (!isDefined(order.stopActivationPrice) || !isDefined(lastPrice) || lastPrice === 0) return unknownValue;
      const distance = order.stopActivationPrice - lastPrice;
      const percent = distance / lastPrice;
      return `${formatCurrency(distance, order.instrument ?? null)} (${formatPercentage(percent)})`;
    }, [order.stopActivationPrice, order.instrument, lastPrice, formatCurrency, formatPercentage]);

    const showPrice = isLimitOrderType(order.orderType);
    const showStopPrice = isStopActivationOrderType(order.orderType);
    const showBoth = showPrice && showStopPrice;

    if (!showPrice && !showStopPrice) {
      return null;
    }

    const limitPriceInput = showPrice && (
      <OrderFormNumberInput
        className={showBoth ? classes.limitInput : undefined}
        label={translation.orderModal.limit}
        value={order.price}
        onChange={handleLimitPriceChange}
        suffix={order.instrument?.currency}
        decimalScale={PRICE_INPUT_DECIMAL_SCALE}
        disabled={disabled}
        testId="order-form-limit-price-input"
        subRows={[{ id: 'price-diff', content: limitPriceDistance }]}
        maxFontSize={PRICE_INPUT_MAX_FONT_SIZE}
        maxSuffixFontSize={PRICE_INPUT_SUFFIX_MAX_FONT_SIZE}
        readOnly={readOnly}
        isLoading={isLoading || priceValidationState?.isLoading}
        errorText={priceValidationState?.errorMessage}
        warningText={priceValidationState?.warningMessage}
      />
    );

    const pricePresetSelector = showPrice && (
      <PricePresetSelector
        className={showBoth ? classes.pricePresets : undefined}
        currentValue={order.price}
        onChange={handleLimitPriceChange}
        bestBid={suggestedPrices.bestBid}
        bestAsk={suggestedPrices.bestAsk}
        midPrice={suggestedPrices.midPrice}
        disabled={disabled}
        readOnly={readOnly}
      />
    );

    const stopPriceInput = showStopPrice && (
      <OrderFormNumberInput
        className={showBoth ? classes.stopInput : undefined}
        label={translation.orderModal.stopPrice}
        value={order.stopActivationPrice}
        onChange={handleStopPriceChange}
        suffix={order.instrument?.currency}
        decimalScale={PRICE_INPUT_DECIMAL_SCALE}
        disabled={disabled}
        readOnly={readOnly}
        isLoading={isLoading || stopActivationPriceValidationState?.isLoading}
        errorText={stopActivationPriceValidationState?.errorMessage}
        testId="order-form-stop-price-input"
        subRows={[{ id: 'stop-price-diff', content: stopPriceDistance }]}
        maxFontSize={PRICE_INPUT_MAX_FONT_SIZE}
        maxSuffixFontSize={PRICE_INPUT_SUFFIX_MAX_FONT_SIZE}
        warningText={stopActivationPriceValidationState?.warningMessage}
      />
    );

    if (!showBoth) {
      return showPrice ? (
        <div className={classes.limitColumn}>
          {limitPriceInput}
          {pricePresetSelector}
        </div>
      ) : (
        stopPriceInput
      );
    }

    return (
      <div className={classes.wrapper}>
        <div className={classes.bothInputs}>
          {limitPriceInput}
          {stopPriceInput}
          {pricePresetSelector}
        </div>
      </div>
    );
  },
);
